<template>
  <div class="relative flex aspect-square w-full max-w-[700px] flex-col-reverse gap-4 md:aspect-[8/7] md:flex-row">
    <ClientOnly>
      <SfScrollable
        class="items-center [-ms-overflow-style:none] [scrollbar-width:none] md:w-full [&::-webkit-scrollbar]:hidden"
        :direction="isMobile ? 'horizontal' : 'vertical'"
        :active-index="activeIndex"
        :previous-disabled="activeIndex === 0"
        :next-disabled="activeIndex === images.length - 1"
        buttons-placement="none"
      >
        <template v-if="images.length">
          <UiButton
            v-for="({ src, thumbSrc, label }, index) in images"
            :id="`product-image-${index}-thumbnail`"
            :key="`image-${index}-thumbnail`"
            variant="none"
            :aria-label="label"
            :aria-current="activeIndex === index"
            :class="[
              'relative grow cursor-pointer overflow-hidden !rounded-md border-2 !p-0 md:h-auto md:w-full md:grow-0',
              activeIndex === index ? 'border-primary-500' : 'border-transparent',
            ]"
            @mouseover="activeIndex = index"
            @focus="activeIndex = index"
          >
            <UiImage :id="`product-thumbnail-${index}`" :alt="label" :src="thumbSrc ?? src" class="w-20" />
          </UiButton>
        </template>
        <div
          v-else
          class="relative grow cursor-pointer overflow-hidden !rounded-md border-2 p-2 md:h-auto md:w-full md:grow-0"
        >
          <LogoSmall class="size-20 fill-neutral-300" />
        </div>
      </SfScrollable>
      <SfScrollable
        class="size-full [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        :active-index="activeIndex"
        :direction="isMobile ? 'horizontal' : 'vertical'"
        wrapper-class="h-full min-h-0"
        is-active-index-centered
        buttons-placement="none"
        :drag="{ containerWidth: true }"
        @on-drag-end="onDragged"
      >
        <template v-if="images.length">
          <div
            v-for="({ src, label }, index) in images"
            :key="`${label}-${index}`"
            class="flex w-full flex-row items-center justify-center"
          >
            <UiImage
              :id="`product-image-${index}`"
              :aria-hidden="activeIndex !== index"
              class="h-auto max-h-[700px] w-full rounded-md"
              :alt="label"
              :src="src"
            />
          </div>
        </template>
        <div v-else class="flex h-auto max-h-[700px] w-full justify-center rounded-md">
          <LogoSmall class="size-80 fill-neutral-300 md:size-96" />
        </div>
      </SfScrollable>
      <template #fallback>
        <div class="flex h-auto max-h-[700px] w-full justify-center rounded-md">
          <LogoSmall class="size-96 animate-pulse fill-neutral-300" />
        </div>
      </template>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import type { SfScrollableOnDragEndData } from '@storefront-ui/vue';

import LogoSmall from 'assets/images/logo-small.svg';

import type { GalleryProps } from '~/components/ui/Gallery/types';

const { isMobile } = useBreakpoints();

const props = defineProps<GalleryProps>();

const activeIndex = ref(0);

const onDragged = (event: SfScrollableOnDragEndData): void => {
  if (event.swipeRight && activeIndex.value > 0) {
    activeIndex.value -= 1;
  } else if (event.swipeLeft && activeIndex.value < props.images.length - 1) {
    activeIndex.value += 1;
  }
};
</script>
